import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAtomValue } from 'jotai';
import '../../../recruit/common/Recruit.scss';
import './MinisterInfo.scss';
import MainURL from '../../../../MainURL';
import { userDataAtom } from '../../../../AtomStore';
import Loading from '../../../../components/Loading';
import { useMinisterAccess } from './useMinisterAccess';
import type { MinisterInfoDetail as MinisterInfoDetailData } from './ministerInfoTypes';
import {
  MinisterInfoDetailContent,
  PortfolioContent,
  SECTION_IDS,
  SelfIntroductionContent,
} from './MinisterInfoDetail';

// 사역자정보 상단 '내 정보 보기' — 본인 이력서·자기소개서·포트폴리오를 상세 화면과 같은 형태로 확인
export default function MinisterMyInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useAtomValue(userDataAtom);
  const access = useMinisterAccess();

  // ?id= 가 없으면 공개 동의한 이력서 → 최신 이력서 순으로 기본 선택
  const idParam = new URLSearchParams(location.search).get('id') || '';
  const mainResume = access.resumeList.find((item) => !!Number(item.isPublic)) || access.resumeList[0];
  const resumeId = idParam || mainResume?.id || '';

  const [resume, setResume] = useState<MinisterInfoDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const go = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (!resumeId || !userData.userAccount) {
      setResume(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    axios.get(`${MainURL}/resume/getresume/${resumeId}`, {
      params: { userAccount: userData.userAccount },
    })
      .then((res) => {
        if (cancelled) return;
        if (res.data.success && res.data.resume) {
          const data = res.data.resume;
          setResume({
            ...data,
            id: String(data.id),
            updatedAt: data.updatedAt ? new Date(data.updatedAt).toLocaleDateString('ko-KR') : (data.saveDate || ''),
          });
        } else {
          setResume(null);
        }
      })
      .catch((err) => {
        console.error('내 이력서 조회 실패:', err);
        if (!cancelled) setResume(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resumeId, userData.userAccount]);

  const renderBody = () => {
    if (access.blockReason === 'login') {
      return (
        <div className="minister-info__guard">
          <h3>로그인이 필요합니다</h3>
          <p>로그인 후 나의 이력서·자기소개서·포트폴리오를 확인할 수 있습니다.</p>
          <button type="button" className="minister-info__guard-btn" onClick={() => go('/login')}>
            로그인하기
          </button>
        </div>
      );
    }
    if (access.loading || isLoading) {
      return (
        <div className="list-loading">
          <Loading />
        </div>
      );
    }
    if (access.resumeList.length === 0) {
      return (
        <div className="minister-info__guard">
          <h3>아직 등록된 이력서가 없습니다</h3>
          <p>이력서를 작성하면 자기소개서와 사역 포트폴리오도 함께 등록할 수 있어요.</p>
          <button type="button" className="minister-info__guard-btn" onClick={() => go('/mypage/resumeedit')}>
            이력서 작성하기
          </button>
        </div>
      );
    }
    if (!resume) {
      return (
        <div className="minister-info__guard">
          <h3>이력서를 불러오지 못했습니다</h3>
          <p>잠시 후 다시 시도해주세요.</p>
        </div>
      );
    }

    const editPath = `/mypage/resumeedit?id=${resume.id}`;
    return (
      <>
        <section style={{ marginBottom: '30px' }}>
          <div className="main_title_row">
            <h3 className="main_title">{resume.title || `${resume.name} 님의 이력서`}</h3>
            <div className="list-btn" onClick={() => go(editPath)}>
              <span>수정하기</span>
            </div>
          </div>
          <div className="minister-info__my-meta">
            <p>수정일 {resume.updatedAt}</p>
            {access.resumeList.length > 1 && (
              <select
                value={resume.id}
                onChange={(e) => navigate(`/minister/my?id=${e.target.value}`, { replace: true })}
              >
                {access.resumeList.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title}{Number(item.isPublic) ? ' (공개)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        </section>

        <div id={SECTION_IDS.resume} className="minister-info__block">
          <h2 className="minister-info__block-title">이력서</h2>
          <MinisterInfoDetailContent resume={resume} />
        </div>

        <div id={SECTION_IDS.intro} className="minister-info__block">
          <h2 className="minister-info__block-title">자기소개서</h2>
          <SelfIntroductionContent resume={resume} />
        </div>

        <div id={SECTION_IDS.portfolio} className="minister-info__block">
          <h2 className="minister-info__block-title">
            사역 포트폴리오
            {(resume.portfolioItems?.length ?? 0) > 0 && <span>{resume.portfolioItems?.length}</span>}
          </h2>
          <PortfolioContent resume={resume} />
        </div>
      </>
    );
  };

  return (
    <div className="recruit minister-info">
      <div className="inner">
        <div className="subpage__main">
          <div className="subpage__main__title">
            <h3>내 정보 보기</h3>
          </div>
          {renderBody()}

          <div className="bottom-list-btn-wrap">
            <div className="bottom-list-btn-inner">
              <div className="list-btn list-btn--bottom" onClick={() => go('/minister')}>
                <span>목록으로</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
